import { useCallback, useEffect, useState } from "react";
import {
  createResendDomain,
  createResendTemplate,
  deleteResendTemplate,
  duplicateResendTemplate,
  getResendConfigHealth,
  getResendDomains,
  getResendEvents,
  getResendSummary,
  getTransactionalMailPreviews,
  getResendTemplate,
  listResendContacts,
  listResendSegments,
  listResendTemplates,
  listResendTopics,
  publishResendTemplate,
  runResendBroadcastPilot,
  sendResendTemplateTest,
  type TransactionalMailPreviewItem,
  updateResendTemplate,
  upsertResendContact,
  verifyResendDomain,
} from "../api/adminResendApi";
import { useToast } from "../context/ToastContext";
import { usePermissionCheck } from "../hooks/usePermissionCheck";
import AdminErrorState from "../Components/AdminErrorState";

type TemplateEditorState = {
  id?: string;
  name: string;
  alias: string;
  subject: string;
  from: string;
  html: string;
  text: string;
};

const EMPTY_TEMPLATE: TemplateEditorState = {
  name: "",
  alias: "",
  subject: "",
  from: "",
  html: "<p>Hello {{{name}}}</p>",
  text: "Hello {{name}}",
};

export default function AdminResendCenter() {
  const toast = useToast();
  const { loading, allowed } = usePermissionCheck("SiteSettings.Manage");

  const [busy, setBusy] = useState(false);
  const [health, setHealth] = useState<any | null>(null);
  const [domains, setDomains] = useState<any[]>([]);
  const [newDomainName, setNewDomainName] = useState("");

  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [templateEditor, setTemplateEditor] = useState<TemplateEditorState>(EMPTY_TEMPLATE);
  const [testEmail, setTestEmail] = useState("");

  const [summary, setSummary] = useState<any | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [mailPreviews, setMailPreviews] = useState<TransactionalMailPreviewItem[]>([]);
  const [selectedPreviewEvent, setSelectedPreviewEvent] = useState("email_verification");

  const [contacts, setContacts] = useState<any[]>([]);
  const [segments, setSegments] = useState<any[]>([]);
  const [topics, setTopics] = useState<any[]>([]);
  const [contactEmail, setContactEmail] = useState("");
  const [contactFirstName, setContactFirstName] = useState("");
  const [contactLastName, setContactLastName] = useState("");

  const [broadcastAudienceId, setBroadcastAudienceId] = useState("");
  const [broadcastName, setBroadcastName] = useState("pilot-broadcast");
  const [broadcastSubject, setBroadcastSubject] = useState("Zenitoon pilot campaign");
  const [broadcastHtml, setBroadcastHtml] = useState("<p>Pilot yayinda oldugunuz icin tesekkurler.</p>");

  const refreshAll = useCallback(async () => {
    setBusy(true);
    const [healthData, domainData, templateData, summaryData, eventData, contactData, segmentData, topicData, previewData] = await Promise.all([
      getResendConfigHealth(),
      getResendDomains(),
      listResendTemplates(),
      getResendSummary(7),
      getResendEvents({ days: 7, pageSize: 25 }),
      listResendContacts(),
      listResendSegments(),
      listResendTopics(),
      getTransactionalMailPreviews(),
    ]);

    setHealth(healthData);
    setDomains(domainData);
    setTemplates(templateData);
    setSummary(summaryData);
    setEvents(eventData?.items ?? []);
    setContacts(contactData);
    setSegments(segmentData);
    setTopics(topicData);
    setMailPreviews(previewData);
    setSelectedPreviewEvent((current) => {
      if (previewData.length === 0) {
        return current;
      }

      return previewData.some((item) => item.eventType === current) ? current : previewData[0].eventType;
    });
    setBusy(false);
  }, []);

  useEffect(() => {
    if (!allowed) {
      return;
    }
    void refreshAll();
  }, [allowed, refreshAll]);

  useEffect(() => {
    if (!selectedTemplateId) {
      return;
    }

    void (async () => {
      const detail = await getResendTemplate(selectedTemplateId);
      if (!detail) {
        return;
      }

      setTemplateEditor({
        id: String(detail.id ?? selectedTemplateId),
        name: String(detail.name ?? ""),
        alias: String(detail.alias ?? ""),
        subject: String(detail.subject ?? ""),
        from: String(detail.from ?? ""),
        html: String(detail.html ?? ""),
        text: String(detail.text ?? ""),
      });
    })();
  }, [selectedTemplateId]);

  if (loading) {
    return <div className="workspace-panel rounded-2xl p-6 text-sm text-slate-600">Kontrol ediliyor...</div>;
  }

  if (!allowed) {
    return (
      <AdminErrorState
        status="warning"
        title="Izniniz yok"
        description="Resend merkezi icin SiteSettings.Manage yetkisi gerekir."
        helpHref="/admin/platform?tab=help"
      />
    );
  }

  const saveTemplate = async () => {
    const payload = {
      name: templateEditor.name.trim(),
      alias: templateEditor.alias.trim() || undefined,
      subject: templateEditor.subject.trim(),
      from: templateEditor.from.trim() || undefined,
      html: templateEditor.html,
      text: templateEditor.text,
    };

    if (!payload.name || !payload.html) {
      toast.error("Template name ve html zorunlu.");
      return;
    }

    const ok = templateEditor.id
      ? await updateResendTemplate(templateEditor.id, payload)
      : await createResendTemplate(payload);

    if (!ok) {
      toast.error("Template kaydedilemedi.");
      return;
    }

    toast.success("Template kaydedildi.");
    setTemplateEditor(EMPTY_TEMPLATE);
    setSelectedTemplateId("");
    await refreshAll();
  };

  const createDomain = async () => {
    if (!newDomainName.trim()) {
      toast.error("Domain giriniz.");
      return;
    }

    const ok = await createResendDomain(newDomainName.trim());
    if (!ok) {
      toast.error("Domain olusturulamadi.");
      return;
    }

    toast.success("Domain olusturuldu.");
    setNewDomainName("");
    await refreshAll();
  };

  const saveContact = async () => {
    if (!contactEmail.trim()) {
      toast.error("Contact e-postasi gerekli.");
      return;
    }

    const ok = await upsertResendContact({
      email: contactEmail.trim(),
      firstName: contactFirstName.trim() || undefined,
      lastName: contactLastName.trim() || undefined,
      unsubscribed: false,
    });

    if (!ok) {
      toast.error("Contact kaydi basarisiz.");
      return;
    }

    toast.success("Contact kaydi guncellendi.");
    setContactEmail("");
    setContactFirstName("");
    setContactLastName("");
    await refreshAll();
  };

  const runPilot = async () => {
    if (!broadcastAudienceId.trim() || !broadcastSubject.trim() || !broadcastHtml.trim()) {
      toast.error("Pilot icin audience + subject + html zorunlu.");
      return;
    }

    const ok = await runResendBroadcastPilot({
      approved: true,
      dryRun: true,
      audienceId: broadcastAudienceId.trim(),
      name: broadcastName.trim() || "pilot-broadcast",
      subject: broadcastSubject.trim(),
      html: broadcastHtml,
    });

    if (!ok) {
      toast.error("Broadcast pilot tetiklenemedi.");
      return;
    }

    toast.success("Broadcast pilot tetiklendi.");
  };

  const activePreview = mailPreviews.find((item) => item.eventType === selectedPreviewEvent) ?? mailPreviews[0] ?? null;

  return (
    <div className="space-y-5 workspace-canvas">
      <section className="workspace-panel rounded-3xl p-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Resend Control Center</h2>
            <p className="text-sm text-slate-600">Template, webhook, growth ve broadcast pilot akislarinin tek noktadan yonetimi.</p>
          </div>
          <button
            type="button"
            onClick={() => void refreshAll()}
            className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700"
            disabled={busy}
          >
            {busy ? "Yenileniyor..." : "Yenile"}
          </button>
        </div>
      </section>

      <section className="workspace-panel rounded-3xl p-6">
        <h3 className="text-base font-semibold text-slate-900">Transactional Mail Preview</h3>
        <p className="mt-1 text-xs text-slate-600">Backend tarafinda uretilen mail govdelerini event tipine gore onizleyebilirsin.</p>
        <div className="mt-3 grid gap-3 lg:grid-cols-[320px,1fr]">
          <div className="space-y-2">
            {mailPreviews.map((preview) => {
              const active = activePreview?.eventType === preview.eventType;
              return (
                <button
                  key={preview.eventType}
                  type="button"
                  onClick={() => setSelectedPreviewEvent(preview.eventType)}
                  className={`w-full rounded-xl border px-3 py-2 text-left ${active ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-white/80"}`}
                >
                  <div className="text-sm font-semibold text-slate-800">{preview.displayName}</div>
                  <div className="text-[11px] text-slate-500">{preview.eventType}</div>
                </button>
              );
            })}
            {mailPreviews.length === 0 && <div className="rounded-xl border border-slate-200 bg-white/80 px-3 py-2 text-xs text-slate-500">Preview verisi yuklenemedi.</div>}
          </div>
          <div className="space-y-3 rounded-2xl border border-slate-200 bg-white/85 p-4">
            {!activePreview ? (
              <div className="text-sm text-slate-500">Preview secilmedi.</div>
            ) : (
              <>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-500">Source</div>
                  <div className="text-sm font-semibold text-slate-900">{activePreview.source}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-500">Subject</div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-800">{activePreview.subject}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-500">HTML</div>
                  <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700">
                    <div dangerouslySetInnerHTML={{ __html: activePreview.htmlBody }} />
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-500">Plain text</div>
                  <pre className="max-h-44 overflow-y-auto whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                    {activePreview.plainTextBody}
                  </pre>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-500">Sample variables</div>
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700">
                    {Object.entries(activePreview.sampleVariables ?? {}).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between gap-2 py-1">
                        <span className="font-semibold text-slate-800">{key}</span>
                        <span className="truncate text-slate-600">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="workspace-panel rounded-3xl p-6">
        <h3 className="text-base font-semibold text-slate-900">Config Health + Domain Operations</h3>
        <div className="mt-3 grid gap-3 md:grid-cols-4">
          <Stat label="Ready" value={String(Boolean(health?.ready))} />
          <Stat label="Base URL" value={String(health?.baseUrl ?? "-")} />
          <Stat label="Domain Probe" value={String(health?.domainProbe?.statusCode ?? "-")} />
          <Stat label="Domain Probe OK" value={String(Boolean(health?.domainProbe?.success))} />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <input
            value={newDomainName}
            onChange={(event) => setNewDomainName(event.target.value)}
            placeholder="mail.zenitoon.com"
            className="rounded border border-slate-300 px-3 py-2 text-sm"
          />
          <button type="button" onClick={() => void createDomain()} className="rounded bg-emerald-600 px-3 py-2 text-xs font-semibold text-white">
            Domain olustur
          </button>
        </div>
        <div className="mt-4 space-y-2">
          {domains.map((domain) => (
            <div key={String(domain?.id ?? domain?.name ?? Math.random())} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white/75 px-3 py-2 text-sm">
              <div>
                <div className="font-semibold text-slate-800">{String(domain?.name ?? "-")}</div>
                <div className="text-xs text-slate-500">status: {String(domain?.status ?? "-")}</div>
              </div>
              <button
                type="button"
                onClick={() => void verifyResendDomain(String(domain?.id ?? ""))}
                className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700"
              >
                Verify
              </button>
            </div>
          ))}
          {domains.length === 0 && <div className="text-xs text-slate-500">Domain bulunamadi.</div>}
        </div>
      </section>

      <section className="workspace-panel rounded-3xl p-6">
        <h3 className="text-base font-semibold text-slate-900">Template CRUD + Publish + Test Send</h3>
        <div className="mt-3 grid gap-4 lg:grid-cols-[300px,1fr]">
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => {
                setSelectedTemplateId("");
                setTemplateEditor(EMPTY_TEMPLATE);
              }}
              className="w-full rounded border border-slate-300 px-3 py-2 text-xs text-slate-700"
            >
              + Yeni template
            </button>
            {templates.map((template) => {
              const id = String(template?.id ?? "");
              const active = selectedTemplateId === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setSelectedTemplateId(id)}
                  className={`w-full rounded-xl border px-3 py-2 text-left ${active ? "border-sky-500 bg-sky-50" : "border-slate-200 bg-white/80"}`}
                >
                  <div className="text-sm font-semibold text-slate-800">{String(template?.name ?? "-")}</div>
                  <div className="text-[11px] text-slate-500">status: {String(template?.status ?? "-")}</div>
                </button>
              );
            })}
          </div>
          <div className="space-y-3 rounded-2xl border border-slate-200 bg-white/80 p-4">
            <div className="grid gap-3 md:grid-cols-2">
              <Input label="Name" value={templateEditor.name} onChange={(value) => setTemplateEditor((prev) => ({ ...prev, name: value }))} />
              <Input label="Alias" value={templateEditor.alias} onChange={(value) => setTemplateEditor((prev) => ({ ...prev, alias: value }))} />
              <Input label="Subject" value={templateEditor.subject} onChange={(value) => setTemplateEditor((prev) => ({ ...prev, subject: value }))} />
              <Input label="From" value={templateEditor.from} onChange={(value) => setTemplateEditor((prev) => ({ ...prev, from: value }))} />
            </div>
            <label className="block text-xs font-semibold text-slate-600">
              HTML
              <textarea value={templateEditor.html} onChange={(event) => setTemplateEditor((prev) => ({ ...prev, html: event.target.value }))} rows={6} className="mt-1 w-full rounded border border-slate-300 px-3 py-2 font-mono text-xs" />
            </label>
            <label className="block text-xs font-semibold text-slate-600">
              Text
              <textarea value={templateEditor.text} onChange={(event) => setTemplateEditor((prev) => ({ ...prev, text: event.target.value }))} rows={3} className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-xs" />
            </label>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => void saveTemplate()} className="rounded bg-emerald-600 px-3 py-2 text-xs font-semibold text-white">Kaydet</button>
              {templateEditor.id && (
                <>
                  <button type="button" onClick={() => void publishResendTemplate(templateEditor.id!)} className="rounded border border-slate-300 px-3 py-2 text-xs text-slate-700">
                    Publish
                  </button>
                  <button type="button" onClick={() => void duplicateResendTemplate(templateEditor.id!)} className="rounded border border-slate-300 px-3 py-2 text-xs text-slate-700">
                    Duplicate
                  </button>
                  <button type="button" onClick={() => void deleteResendTemplate(templateEditor.id!)} className="rounded border border-rose-300 px-3 py-2 text-xs text-rose-700">
                    Delete
                  </button>
                </>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
              <input
                value={testEmail}
                onChange={(event) => setTestEmail(event.target.value)}
                placeholder="test@zenitoon.com"
                className="rounded border border-slate-300 px-3 py-2 text-xs"
              />
              <button
                type="button"
                onClick={async () => {
                  if (!templateEditor.id || !testEmail.trim()) {
                    toast.error("Template ve test e-postasi gerekli.");
                    return;
                  }
                  const ok = await sendResendTemplateTest(templateEditor.id, testEmail.trim(), { name: "Zenitoon" });
                  if (!ok) {
                    toast.error("Test mail gonderilemedi.");
                    return;
                  }
                  toast.success("Test mail gonderildi.");
                }}
                className="rounded bg-slate-900 px-3 py-2 text-xs font-semibold text-white"
              >
                Test send
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="workspace-panel rounded-3xl p-6">
        <h3 className="text-base font-semibold text-slate-900">Ops Dashboard + Event Drilldown</h3>
        <div className="mt-3 grid gap-3 md:grid-cols-5">
          <Stat label="Mail total" value={String(summary?.mail?.total ?? 0)} />
          <Stat label="Delivered" value={String(summary?.webhook?.funnel?.delivered ?? 0)} />
          <Stat label="Bounced" value={String(summary?.webhook?.funnel?.bounced ?? 0)} />
          <Stat label="Complained" value={String(summary?.webhook?.funnel?.complained ?? 0)} />
          <Stat label="Opened" value={String(summary?.webhook?.funnel?.opened ?? 0)} />
        </div>
        <div className="mt-4 max-h-56 space-y-2 overflow-y-auto rounded-xl border border-slate-200 bg-white/75 p-3">
          {events.map((event) => (
            <div key={String(event.id)} className="rounded border border-slate-200 bg-white px-3 py-2 text-xs">
              <div className="font-semibold text-slate-800">{event.area} / {event.action}</div>
              <div className="text-slate-500">{new Date(event.occurredAtUtc).toLocaleString("tr-TR")}</div>
            </div>
          ))}
          {events.length === 0 && <div className="text-xs text-slate-500">Event bulunamadi.</div>}
        </div>
      </section>

      <section className="workspace-panel rounded-3xl p-6">
        <h3 className="text-base font-semibold text-slate-900">Contacts + Segments + Topics + Broadcast Pilot</h3>
        <div className="mt-3 grid gap-4 md:grid-cols-2">
          <div className="space-y-2 rounded-2xl border border-slate-200 bg-white/80 p-4">
            <div className="text-sm font-semibold text-slate-800">Contact upsert</div>
            <Input label="Email" value={contactEmail} onChange={setContactEmail} />
            <Input label="First name" value={contactFirstName} onChange={setContactFirstName} />
            <Input label="Last name" value={contactLastName} onChange={setContactLastName} />
            <button type="button" onClick={() => void saveContact()} className="rounded bg-emerald-600 px-3 py-2 text-xs font-semibold text-white">
              Contact kaydet
            </button>
            <div className="pt-2 text-xs text-slate-500">Toplam contact: {contacts.length}</div>
          </div>
          <div className="space-y-2 rounded-2xl border border-slate-200 bg-white/80 p-4">
            <div className="text-sm font-semibold text-slate-800">Broadcast pilot (dry-run)</div>
            <Input label="Audience ID" value={broadcastAudienceId} onChange={setBroadcastAudienceId} />
            <Input label="Name" value={broadcastName} onChange={setBroadcastName} />
            <Input label="Subject" value={broadcastSubject} onChange={setBroadcastSubject} />
            <label className="block text-xs font-semibold text-slate-600">
              HTML
              <textarea value={broadcastHtml} onChange={(event) => setBroadcastHtml(event.target.value)} rows={4} className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-xs" />
            </label>
            <button type="button" onClick={() => void runPilot()} className="rounded bg-slate-900 px-3 py-2 text-xs font-semibold text-white">
              Pilot tetikle
            </button>
          </div>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white/75 p-3 text-xs">
            <div className="font-semibold text-slate-800">Segments ({segments.length})</div>
            {segments.slice(0, 8).map((item) => (
              <div key={String(item?.id ?? Math.random())} className="text-slate-600">{String(item?.name ?? item?.id ?? "-")}</div>
            ))}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white/75 p-3 text-xs">
            <div className="font-semibold text-slate-800">Topics ({topics.length})</div>
            {topics.slice(0, 8).map((item) => (
              <div key={String(item?.id ?? Math.random())} className="text-slate-600">{String(item?.name ?? item?.id ?? "-")}</div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white/80 p-3 text-xs">
      <div className="text-[11px] uppercase text-slate-500">{label}</div>
      <div className="mt-1 text-base font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function Input({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block text-xs font-semibold text-slate-600">
      {label}
      <input value={value} onChange={(event) => onChange(event.target.value)} className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-xs" />
    </label>
  );
}
